import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import { useTranslation } from "@coworker/locales";
import styles from "./AddComment.module.css";
import { AddImagesModal } from "../AddImagesModal/AddImagesModal";
import { useCallback, useMemo, useState } from "react";
import {
  filesToNewFiles,
  NewOrExistingFile,
  newOrExistingFilesToFiles,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import InputWrapper from "../InputWrapper/InputWrapper";
import { MAX_COMMENT_TEXT_LENGTH } from "../../types/Comment";
import { applyRotations } from "@coworker/apprestructured/src/task-management/util";

type AddCommentProps = {
  text: string;
  images: File[];
  disableAllInputs: boolean;
  disableAddButton: boolean;
  onTextChange: (text: string) => void;
  onImagesChange: (images: File[]) => void;
  onAddClick: () => void;
};

export const AddComment = ({
  text,
  images,
  disableAllInputs,
  disableAddButton,
  onTextChange,
  onImagesChange,
  onAddClick,
}: AddCommentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<NewOrExistingFile[]>([]);
  const [showRequiredError, setShowRequiredError] = useState(false);

  const { t } = useTranslation();

  const handleAddImagesButtonClick = useCallback(() => {
    setModalImages(filesToNewFiles(images));
    setIsModalOpen(true);
  }, [images]);

  const handleAddImagesModalConfirmClick = useCallback(async () => {
    try {
      const processedImages = await applyRotations(modalImages);
      onImagesChange(newOrExistingFilesToFiles(processedImages));
    } catch (error) {
      console.error("Failed to apply image rotations", error);
    }
  }, [modalImages, onImagesChange]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const textError = useMemo(() => {
    if (text.length > MAX_COMMENT_TEXT_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_COMMENT_TEXT_LENGTH,
      });
    }

    if (showRequiredError && !text.trim() && images.length === 0) {
      return t("pleaseAddCommentString");
    }

    return null;
  }, [images.length, showRequiredError, text, t]);

  const handleAddCommentClick = useCallback(() => {
    if (!text.trim() && images.length === 0) {
      setShowRequiredError(true);
      return;
    }

    if (text.length <= MAX_COMMENT_TEXT_LENGTH) {
      setShowRequiredError(false);
      onAddClick();
    }
  }, [images.length, onAddClick, text]);

  return (
    <div className={styles["addComment"]}>
      <InputWrapper error={textError}>
        <FixaTextArea
          value={text}
          disabled={disableAllInputs}
          label={t("addCommentString")}
          onChange={(e) => {
            const nextText = e.target.value;
            if (showRequiredError && (nextText.trim() || images.length > 0)) {
              setShowRequiredError(false);
            }
            onTextChange(nextText);
          }}
        />
      </InputWrapper>
      <div className={styles["buttonWrapper"]}>
        <FixaButton
          size="xsmall"
          text={
            images.length > 0
              ? `${t("addImagesString")} (${images.length})`
              : t("addImagesString")
          }
          type="secondary"
          disabled={disableAllInputs}
          ssrIcon={ImageIconPath}
          onClick={handleAddImagesButtonClick}
        />
        <FixaButton
          size="xsmall"
          text={t("addString")}
          type="emphasised"
          disabled={disableAllInputs || disableAddButton}
          onClick={handleAddCommentClick}
        />
      </div>
      <AddImagesModal
        isOpen={isModalOpen}
        images={modalImages}
        onImagesChange={setModalImages}
        onClose={handleCloseModal}
        onConfirmClick={handleAddImagesModalConfirmClick}
      />
    </div>
  );
};
